/**
 * #723 — attach "why this matters" triggers to the map's watch cards.
 *
 * The map card (compass-map `WatchCardPanel`) renders `watch.triggers` when a
 * watch carries them and invents nothing when it does not. The trigger table
 * lives HERE, in `intelModel.WATCH_TYPES`, so INTEL and the map read the same
 * terms for the same hazard — this module is the only bridge and must never
 * grow a second table.
 *
 * Every export fails safe: a non-array, a null entry, a card without a
 * `typeKey`, or an unmapped `typeKey` yields the input untouched.
 */
import { watchTypeFor } from "./intelModel";

/**
 * Triggers for one mapped card: the card's own `triggers` when it already
 * carries a non-empty list (the INTEL detail shape wins), else the terms for
 * its `typeKey`, else [].
 * @param {object|null|undefined} card
 * @returns {{ term: string, text: string }[]}
 */
export function triggersForCard(card) {
  if (!card || typeof card !== "object") return [];
  if (Array.isArray(card.triggers) && card.triggers.length) {
    return card.triggers
      .filter((t) => t && typeof t === "object" && typeof t.term === "string" && t.term.trim())
      .map((t) => ({ term: t.term.trim(), text: typeof t.text === "string" ? t.text : "" }));
  }
  const type = watchTypeFor(card.typeKey);
  if (!type || !Array.isArray(type.triggers)) return [];
  return type.triggers.map((term) => ({ term, text: "" }));
}

/**
 * Zones / corridors from the compass-map mappers, each carrying the SAME
 * `card` the INTEL rail renders, with `card.triggers` attached where the
 * hazard maps to a WATCH_TYPES entry. New objects, never mutation; entries
 * without a card, or whose card yields no triggers, pass through unchanged.
 * @param {object[]} overlays
 * @returns {object[]}
 */
export function attachTriggersToOverlays(overlays) {
  if (!Array.isArray(overlays)) return [];
  return overlays.map((overlay) => {
    if (!overlay || typeof overlay !== "object" || !overlay.card) return overlay;
    const triggers = triggersForCard(overlay.card);
    if (!triggers.length) return overlay;
    return { ...overlay, card: { ...overlay.card, triggers } };
  });
}
